import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import GameDataComparison from '../GameDataComparison';
import { getSocialOverallIndex } from '@home/services/studio';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import './index.scss';

/**
 * 游戏数据对比组件的属性接口
 * @param {string} className - 组件的类名
 * @param {React.CSSProperties} style - 组件的内联样式
 * @param {string} titleEn - 组件标题的英文文本
 * @param {string} titleCn - 组件标题的中文文本
 * @param {string} tipContentEn - 组件提示内容的英文文本
 * @param {string} tipContentCn - 组件提示内容的中文文本
 * @param {any} filter - 组件的筛选条件
 * @param {React.CSSProperties} normalStyle - 组件的正常显示样式
 */
interface IGameDataComparisonProps {
  className: string;
  style: React.CSSProperties;
  titleEn: string;
  titleCn: string;
  tipContentEn: string;
  tipContentCn: string;
  filter: any;
  maxlength?: number;
  gameMap: any;
  gameInfo: any;
  languageMap: any;
}

export default (props: IGameDataComparisonProps) => {
  const {
    className,
    style,
    titleEn,
    titleCn,
    filter,
    tipContentEn,
    tipContentCn,
    maxlength,
    gameMap,
    gameInfo = {},
    languageMap
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const title = getName(isEn, titleEn, titleCn);
  const titleTip = getName(isEn, tipContentEn, tipContentCn);

  const { start_time, end_time, date_type, language, game } = filter || {};

  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    if (!game) return;
    setLoading(true);
    const uid = gameInfo?.edition_unified_id || '';
    getSocialOverallIndex({
      "unified_id": uid,
      "start_date": dateTimeFormat(start_time, 'day'),
      "end_date": dateTimeFormat(end_time, 'day'),
      "period": date_type,
      "filters": {
        "unified_edition_id_list": [uid, game],
        "language_list": language
      },
      // start_date: '2024-01-08',
      // end_date: '2024-01-14',
      metrics: ['overall_index'],
      accumulation: {
        by_unified_edition_id: true,
        by_language: true,
      },
    }).then((data) => {
      setData(data?.metrics_list);
    }).catch(() => {
      setIsError(true);
    }).finally(() => {
      setLoading(false);
    });
  }, [filter, gameInfo?.edition_unified_id, game, start_time, end_time, date_type, language]);

  const { gameList, dataList } = useMemo(() => {
    // 获取2个游戏信息
    const currentGameId = gameInfo?.edition_unified_id || '';
    const compareGameId = filter?.game || '';
    const currentGameInfo = gameMap?.[currentGameId] || {};
    const competingGameInfo = gameMap?.[compareGameId] || {};

    const gameList = [
      { ...currentGameInfo, color: '#5086F3' },
      { ...competingGameInfo, color: '#F19E55' },
    ];

    const dataList = language?.map((lang: string) => {
      const langInfo = languageMap[lang] || {};
      const dataItems = data?.filter((item) => item?.language === lang) || [];
      const currentData =
        dataItems.find((item) => item?.unified_edition_id === currentGameId) ||
        {};
      const compareData =
        dataItems.find(
          (item) => item?.unified_edition_id === compareGameId,
        ) || {};

      return {
        name: langInfo.name,
        valueFormat: 'oneDecimal',
        list: [
          {
            name: currentGameInfo.name,
            color: '#326FF0',
            value: currentData?.metrics?.overall_index?.value || '',
            img: currentGameInfo.cover,
          },
          {
            name: competingGameInfo.name,
            color: '#F19E55',
            value: compareData?.metrics?.overall_index?.value || '',
            img: competingGameInfo.cover,
          },
        ],
      };
    });
    return {
      gameList,
      dataList
    };
  }, [data, filter?.game, gameInfo?.edition_unified_id, gameMap, language, languageMap]);

  return (
    <CardTitleBox
      title={title}
      titleTip={<div dangerouslySetInnerHTML={{ __html: titleTip }}></div>}
      className={classNames('dbh-studio-overall-index-container', className)}
      style={style}
      normalConfig={{
        isLoading: loading,
        isError,
        isNoData: data?.length === 0,
        style: { height: 107 },
      }}
      rightSolt={
        <div className="dbh-studio-overall-index-game-list">
          {gameList.map((item, index) => {
            const { color, name, cover } = item;
            return (
              <div key={index}>
                <div
                  className="dbh-studio-overall-index-game-dot"
                  style={{ background: color }}
                ></div>
                <div className="dbh-studio-overall-index-game-img">
                  <img src={cover} />
                </div>
                <div className="dbh-studio-overall-index-game-text">{name}</div>
              </div>
            );
          })}
        </div>
      }
    >
      <GameDataComparison
        dataList={dataList}
        maxlength={maxlength}
        tooltipTitle={title}
      />
    </CardTitleBox>
  );
};
